import {
  audioUtil,
  cacheManager,
  FuuzeError,
  isConsideredListened,
  Podcast,
  PodcastEpisode,
  podcastUtil,
  type SpotlightLink,
  type TrackBody,
  type TrackReply,
  tryCatch,
} from '@fuuze/shared';
import { Howl } from 'howler';
import i18next from 'i18next';
import superjson from 'superjson';
import { create } from 'zustand';
import { type PersistOptions, persist, type StorageValue, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { fetchService, queryClient } from '../singletons';
import { firebaseUtil } from '../utils/firebase.util';
import { Toast } from '../utils/toast.util';
import { useStore } from './store';

// ----------------------------------------
// Types
// ----------------------------------------

export type PodcastPlaying = { channel: Podcast; item: PodcastEpisode };

type PlayerStoreState = {
  _howl: Howl | undefined;
  duration: number;
  spotlight?: SpotlightLink;
  isLoading: boolean;
  isPlaying: boolean;
  playerCollapsedHeight: number;
  playerPosition: number;
  podcastPlaying: PodcastPlaying | undefined;
  loadError?: boolean;
  listenedSegments: [number, number][];
  // Keep the last upper interval of the segment sent to the server for tracking.
  // We need to keep it because during the interval updating the player position, if we set for example an interval of 1 second
  // each tick won't produce a clean time like 1,2,3,4 but it will produce numbers with multiple decimals, making sometime the rounding
  // inconsistent. Which, when it happens, will send to the backend for example [[0,12]] then [[12,34]] but it should send [[13,34]] because
  // otherwise the system will consider the user listened twice the bucket corresponding to the 12 seconds (if we consider a bucket size of 1 second).
  // So it is helpful to not increment the lower bound of the segment if the current player position is not greater than the last upper bound value send to server
  _lastPositionSentToServer: number | undefined;
};

type PlayerStoreActions = {
  backward: () => void;
  forward: () => void;
  loadAudio: (podcastPlaying: PodcastPlaying, { play }: { play: boolean; setPositionTo?: number }) => Promise<Howl>;
  playPause: () => Promise<void>;
  reset: () => void;
  seek: (value: number) => Promise<void>;
  setSpotlight: (spotlight?: SpotlightLink) => void;
  setPlayerCollapsedHeight: (value: number) => void;
  setPosition: (value: number) => void;
};

type PlayerStore = PlayerStoreState & PlayerStoreActions;
type LocalStorageState = Pick<PlayerStoreState, 'podcastPlaying' | 'listenedSegments' | 'playerPosition'>;

// ----------------------------------------
// Store
// ----------------------------------------

const initialPlayerSliceState: PlayerStoreState = {
  _howl: undefined,
  duration: 0,
  spotlight: undefined,
  isLoading: false,
  isPlaying: false,
  playerCollapsedHeight: 0,
  playerPosition: 0,
  podcastPlaying: undefined,
  listenedSegments: [],
  _lastPositionSentToServer: undefined,
};

const persistConfig: PersistOptions<PlayerStore, LocalStorageState> = {
  name: 'fuuze-player-store',
  storage: {
    getItem: name => {
      const item = localStorage.getItem(name);
      if (!item) return null;
      return superjson.parse<StorageValue<LocalStorageState>>(item);
    },
    setItem: (name, value) => {
      localStorage.setItem(name, superjson.stringify(value));
    },
    removeItem: name => {
      localStorage.removeItem(name);
    },
  },
  partialize: state => ({
    podcastPlaying: state.podcastPlaying,
    listenedSegments: state.listenedSegments,
    playerPosition: state.playerPosition,
  }),
  merge: (persisted, initial) => {
    const { podcastPlaying, listenedSegments, playerPosition } = persisted as LocalStorageState;

    const episode = podcastPlaying ? new PodcastEpisode(podcastPlaying.item) : undefined;
    if (episode?.itemUser) {
      episode.itemUser.position = playerPosition;
    }

    return {
      ...initial,
      listenedSegments,
      podcastPlaying: podcastPlaying ? { item: episode!, channel: new Podcast(podcastPlaying.channel) } : undefined,
    };
  },
};

let playerListenerInterval: ReturnType<typeof setInterval> | undefined;

export const usePlayerStore = create<PlayerStore>()(
  subscribeWithSelector(
    persist(
      immer((set, get) => {
        if (import.meta.hot) {
          import.meta.hot.on('vite:beforeUpdate', () => {
            clearInterval(playerListenerInterval);
          });
        }

        return {
          ...initialPlayerSliceState,
          playPause: async () => {
            const howl = get()._howl;
            const podcastPlaying = get().podcastPlaying;

            if (!howl && podcastPlaying) {
              await get().loadAudio(podcastPlaying, { play: true });
            }

            if (howl?.playing()) {
              howl.pause();
              set({ isPlaying: false });
              await sendTrackInfoToServer();
            } else {
              howl?.play();
              set({ isPlaying: true });
            }
          },
          loadAudio: async (podcastPlaying, { play, setPositionTo = 0 }) => {
            const howl = await load({
              podcastPlaying,
              howl: get()._howl,
              autoplay: play,
              initialSeekTo: setPositionTo,
            });
            set({
              _howl: howl,
              podcastPlaying: podcastPlaying,
            });

            return howl;
          },
          reset: () => {
            set(initialPlayerSliceState);
          },
          seek: async (value: number) => {
            let _howl = get()._howl;
            const podcastPlaying = get().podcastPlaying;
            if (!_howl && podcastPlaying) {
              _howl = await get().loadAudio(podcastPlaying, { play: false });
            }
            _howl?.seek(value);
            set({
              playerPosition: value,
              // If the user seek, he will maybe listened again certain range of audio, and this is intentional.
              // So we remove the _lastPositionSentToServer stored otherwise if he seeks before the _lastPositionSentToServer
              // the tracking will never be sent to the server until his new listening time is greater than the _lastPositionSentToServer
              _lastPositionSentToServer: undefined,
            });
          },
          setPosition: (value: number) => {
            set({ playerPosition: value });
          },
          forward: () => {
            const { playerPosition, podcastPlaying, seek, duration } = get();
            if (!podcastPlaying) return;
            if (playerPosition + 15 < duration) {
              seek(playerPosition + 15);
            } else {
              seek(duration);
            }
          },
          backward: () => {
            const { playerPosition, seek } = get();
            if (playerPosition - 15 > 0) {
              seek(playerPosition - 15);
            } else {
              seek(0);
            }
          },
          setPlayerCollapsedHeight: (value: number) => set({ playerCollapsedHeight: value }),
          setSpotlight: spotlight => set({ spotlight }),
        };
      }),
      persistConfig,
    ),
  ),
);

// ----------------------------------------
// Functions
// ----------------------------------------

function startPlayerListenerInterval() {
  return setInterval(() => {
    const { _howl, podcastPlaying, duration } = usePlayerStore.getState();
    if (_howl?.playing() && podcastPlaying) {
      const position = Math.round(_howl?.seek() || 0);
      usePlayerStore.setState(state => {
        state.playerPosition = position;
      });
      updateListenedSegments();

      // Will be true only the first time the audio is considered listened
      const shouldBeMovedFromNonListenedToListened =
        !podcastPlaying.item.itemUser?.listened && isConsideredListened(position, duration);
      sendTrackInfoToServer({ forceSend: shouldBeMovedFromNonListenedToListened });
    }
  }, 250);
}

async function load(params: {
  podcastPlaying: PodcastPlaying;
  howl?: Howl;
  autoplay?: boolean;
  initialSeekTo?: number;
}): Promise<Howl> {
  const { podcastPlaying, howl, autoplay, initialSeekTo } = params;

  await sendTrackInfoToServer({ forceSend: true });

  // Reset load error and set loading
  usePlayerStore.setState({
    loadError: false,
    isLoading: true,
    // Reset the listened segments in any case, even if the sendTrackInfoToServer does not do it
    listenedSegments: [],
    _lastPositionSentToServer: undefined,
  });

  howl?.unload();

  const newHowl = new Howl({
    src: await audioUtil().retrieveAudioUrl({
      fetchService,
      podcastEpisodeId: podcastPlaying.item._id,
      idToken: firebaseUtil().getIdToken(),
    }),
    html5: true,
    autoplay: !!autoplay,
    preload: true,
    format: 'mp3',
  });

  if (initialSeekTo) newHowl.seek(initialSeekTo);
  else if (podcastPlaying.item.itemUser) {
    newHowl.seek(podcastPlaying.item.itemUser.position);
  }

  newHowl.on('end', () => {
    usePlayerStore.setState({
      isPlaying: false,
    });
  });
  newHowl.on('load', () => {
    usePlayerStore.setState({
      isLoading: false,
      duration: newHowl.duration(),
      playerPosition: initialSeekTo || podcastPlaying.item.itemUser?.position || 0,
      isPlaying: autoplay,
    });
  });
  newHowl.on('pause', () => {
    clearInterval(playerListenerInterval);
  });
  newHowl.on('play', () => {
    playerListenerInterval = startPlayerListenerInterval();

    navigator.mediaSession.metadata = new MediaMetadata({
      title: podcastPlaying.item.title,
      artist: podcastPlaying.item.author,
      artwork: [{ src: podcastPlaying.item.image600, sizes: '300x300', type: 'image/webp' }],
    });

    navigator.mediaSession.setActionHandler('play', () => usePlayerStore.getState().playPause());
    navigator.mediaSession.setActionHandler('pause', () => usePlayerStore.getState().playPause());
    navigator.mediaSession.setActionHandler('seekbackward', () => usePlayerStore.getState().backward());
    navigator.mediaSession.setActionHandler('seekforward', () => usePlayerStore.getState().forward());
  });
  newHowl.on('loaderror', (_, e) => {
    // see: https://github.com/goldfire/howler.js?tab=readme-ov-file#onloaderror-function
    usePlayerStore.getState()._howl?.pause();
    if (e === 1 || e === 2) return;
    Toast.showToast({
      type: 'error',
      data: { title: i18next.t('components.playerCollapsed.loadError') },
    });
    usePlayerStore.setState({ loadError: true, isLoading: false, isPlaying: false, podcastPlaying: undefined });
  });

  return newHowl;
}

let sendTrackInfoLastCall = Date.now();
async function sendTrackInfoToServer(params?: { forceSend?: boolean }) {
  const { forceSend } = params || {};
  const { listenedSegments, podcastPlaying, duration, _howl, playerPosition } = usePlayerStore.getState();

  // Do not send info to server if last call has been made less than 30 seconds ago
  if (!forceSend && Date.now() - sendTrackInfoLastCall < 30_000) return;
  if (listenedSegments.length === 0) return;
  if (!podcastPlaying || !_howl) return;

  const filteredSegments = listenedSegments.filter(
    s => s[1] - s[0] >= podcastUtil().PODCAST_EPISODE_STATS_BUCKET_LISTENING_SIZE,
  );
  if (filteredSegments.length === 0) return;

  const { data, error } = await tryCatch(
    fetchService.post<TrackReply, TrackBody>('/itemUser/track', {
      channelId: podcastPlaying.channel._id,
      itemId: podcastPlaying.item._id,
      duration,
      // Do not send segments smaller than a bucket
      segments: filteredSegments,
      position: Math.round(playerPosition),
    }),
  );

  if (error) {
    console.log(error);
    useStore.getState().errorReporter.capture(new FuuzeError({ message: 'player/track-info', info: error }));
    return;
  }

  if (!data) return;

  cacheManager().updatePodcastEpisode(queryClient, episode => episode._id.toString() === podcastPlaying.item._id, {
    itemUser: data,
  });
  usePlayerStore.setState(state => {
    state.podcastPlaying!.item.itemUser = data;
    // Critical part to have the tracking working properly.
    // Here we set as the last position sent to server the real last position sent but we take the lower bound of the next bucket size.
    // We won't have really precise tracking because the bucket size is > 1 but we don't need so much accuracy
    state._lastPositionSentToServer =
      (podcastUtil().getStatsBucketNumber(state.listenedSegments.at(-1)![1]) + 1) *
      podcastUtil().PODCAST_EPISODE_STATS_BUCKET_LISTENING_SIZE;
    state.listenedSegments = [];
  });

  sendTrackInfoLastCall = Date.now();
}

function updateListenedSegments() {
  usePlayerStore.setState(store => {
    const { listenedSegments, _lastPositionSentToServer, playerPosition } = store;

    if (listenedSegments.length === 0) {
      if (_lastPositionSentToServer !== undefined && playerPosition <= _lastPositionSentToServer) {
        return;
      }
      listenedSegments.push([playerPosition, playerPosition]);
      return;
    }

    const lastSegment = listenedSegments.at(-1)!;

    if (Math.abs(lastSegment[1] - playerPosition) > 1) {
      // User sought
      listenedSegments.push([playerPosition, playerPosition]);
      return;
    }

    lastSegment[1] = playerPosition;
  });
}
